import { useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2, Download, Flame, RotateCcw, Sparkles, Trophy } from "lucide-react";
import { HomeworkShell, useHwAccount } from "./HomeworkShell";
import { hwApi, hwErr } from "./hwApi";
import "../test/testTheme.css";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MILESTONES = [7, 21, 30, 50, 100];
const TIMES = [];
for (let h = 4; h <= 23; h++) for (const m of [0, 30]) TIMES.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
const TimeSel = ({ value, onChange, testid }) => (
  <select className="admin-input wz-ctrl" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={{ minWidth: 110 }}>
    {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
  </select>
);

const DEFAULT_COMMITMENTS = [
  { label: "School / Coaching", start: "08:00", end: "14:00", days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] },
  { label: "Lunch + rest", start: "14:00", end: "15:00", days: DAYS },
  { label: "Dinner", start: "20:30", end: "21:00", days: DAYS },
];

function Wizard({ onDone }) {
  const account = useHwAccount();
  const [step, setStep] = useState(0);
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState({ name: account?.name || "", class_level: "", exam_target: "NEET",
    subjects: ["Physics", "Chemistry", "Biology"], weak_subjects: [], chapters: {}, daily_target_hours: 3, level: "Average" });
  const [routine, setRoutine] = useState({ wake: "06:00", sleep: "22:30", commitments: DEFAULT_COMMITMENTS });
  const [prefs, setPrefs] = useState({});

  useEffect(() => { hwApi.get("/planner/options").then(({ data }) => setOpts(data)).catch(() => setOpts({})); }, []);
  const subjects = opts?.subjects_by_target?.[profile.exam_target] || opts?.all_subjects || [];
  const toggle = (list, v) => list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const steps = ["Profile", "Routine", "Commitments", "Subjects & Chapters", "Generate"];
  const canNext = step !== 0 || (profile.name.trim() && profile.class_level && profile.subjects.length > 0);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await hwApi.post("/planner/plan", { profile, routine, prefs });
      onDone(data);
    } catch (e) { setError(hwErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 720 }} data-testid="planner-wizard">
      <div className="tp-eyebrow">STUDY PLANNER</div>
      <h1 className="tp-h2" style={{ fontSize: 28 }}>Build my study timetable</h1>
      <div style={{ display: "flex", gap: 6, margin: "16px 0 20px" }} data-testid="wizard-progress">
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1 }}>
            <div style={{ height: 6, borderRadius: 3, background: i <= step ? "#f77f00" : "#e4eaf2" }} />
            <span className="tp-note" style={{ fontSize: 10 }}>{i + 1}. {s}</span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="tp-panel">
          <label className="tp-label">YOUR NAME<input className="admin-input" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} data-testid="wz-name" /></label>
          <label className="tp-label">CLASS
            <select className="admin-input" value={profile.class_level} onChange={(e) => setProfile((p) => ({ ...p, class_level: e.target.value }))} data-testid="wz-class">
              <option value="">Select class</option>
              {["Class 9", "Class 10", "Class 11", "Class 12", "Dropper"].map((c) => <option key={c}>{c}</option>)}
            </select></label>
          <label className="tp-label">EXAM TARGET
            <select className="admin-input" value={profile.exam_target} onChange={(e) => {
              const t = e.target.value;
              setProfile((p) => ({ ...p, exam_target: t, subjects: (opts?.subjects_by_target?.[t] || p.subjects) }));
            }} data-testid="wz-target">
              {(opts?.targets || []).map((t) => <option key={t}>{t}</option>)}
            </select></label>
          <label className="tp-label">CURRENT LEVEL
            <select className="admin-input" value={profile.level} onChange={(e) => setProfile((p) => ({ ...p, level: e.target.value }))} data-testid="wz-level">
              {["Beginner", "Average", "Good", "Advanced"].map((l) => <option key={l}>{l}</option>)}
            </select></label>
          <label className="tp-label">DAILY SELF-STUDY TARGET (HOURS)
            <input className="admin-input" type="number" min="1" max="10" value={profile.daily_target_hours}
                   onChange={(e) => setProfile((p) => ({ ...p, daily_target_hours: Number(e.target.value) }))} data-testid="wz-target-hours" /></label>
        </div>
      )}

      {step === 1 && (
        <div className="tp-panel">
          <div className="admin-row" style={{ gap: 16, flexWrap: "wrap" }}>
            <label className="tp-label">WAKE UP<TimeSel value={routine.wake} onChange={(v) => setRoutine((r) => ({ ...r, wake: v }))} testid="wz-wake" /></label>
            <label className="tp-label">SLEEP<TimeSel value={routine.sleep} onChange={(v) => setRoutine((r) => ({ ...r, sleep: v }))} testid="wz-sleep" /></label>
          </div>
          <p className="tp-note">Meals, school and coaching go on the next step as fixed commitments.</p>
        </div>
      )}

      {step === 2 && (
        <div className="tp-panel">
          <p className="tp-note" style={{ marginTop: 0 }}>Your fixed commitments — the planner will never schedule study over these. Tap the day chips to choose which days each one applies to.</p>
          {routine.commitments.map((c, i) => (
            <div key={i} style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid #e4eaf2", borderRadius: 10 }} data-testid={`wz-commit-${i}`}>
              <div className="admin-row" style={{ gap: 8, flexWrap: "wrap" }}>
                <input className="admin-input wz-ctrl" style={{ flex: 1, minWidth: 120 }} value={c.label}
                       onChange={(e) => setRoutine((r) => { const cs = [...r.commitments]; cs[i] = { ...c, label: e.target.value }; return { ...r, commitments: cs }; })} />
                <TimeSel value={c.start} onChange={(v) => setRoutine((r) => { const cs = [...r.commitments]; cs[i] = { ...c, start: v }; return { ...r, commitments: cs }; })} testid={`wz-commit-start-${i}`} />
                <TimeSel value={c.end} onChange={(v) => setRoutine((r) => { const cs = [...r.commitments]; cs[i] = { ...c, end: v }; return { ...r, commitments: cs }; })} testid={`wz-commit-end-${i}`} />
                <button className="admin-btn" onClick={() => setRoutine((r) => ({ ...r, commitments: r.commitments.filter((_, j) => j !== i) }))} data-testid={`wz-commit-del-${i}`}>✕</button>
              </div>
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }} data-testid={`wz-commit-days-${i}`}>
                {DAYS.map((d, di) => (
                  <button key={d} className={`admin-btn ${c.days.includes(d) ? "primary" : ""}`} style={{ fontSize: 10, padding: "4px 8px" }}
                          onClick={() => setRoutine((r) => {
                            const cs = [...r.commitments];
                            const days = c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d];
                            cs[i] = { ...c, days: days.length ? days : c.days };
                            return { ...r, commitments: cs };
                          })}
                          data-testid={`wz-commit-day-${i}-${di}`}>{d.slice(0, 3)}</button>
                ))}
              </div>
            </div>
          ))}
          <button className="admin-btn" onClick={() => setRoutine((r) => ({ ...r, commitments: [...r.commitments, { label: "New activity", start: "17:00", end: "18:00", days: DAYS }] }))} data-testid="wz-commit-add">+ Add commitment</button>
          <p className="tp-note" style={{ marginTop: 8 }}>Example: mark School / Coaching as Mon–Sat, and a Sunday-only test as just Sun.</p>
        </div>
      )}

      {step === 3 && (
        <div className="tp-panel">
          <p className="tp-note" style={{ marginTop: 0 }}>Subjects for {profile.exam_target}:</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {subjects.map((s) => (
              <button key={s} className={`admin-btn ${profile.subjects.includes(s) ? "primary" : ""}`}
                      onClick={() => setProfile((p) => ({ ...p, subjects: toggle(p.subjects, s) }))} data-testid={`wz-subject-${s}`}>{s}</button>
            ))}
          </div>
          <p className="tp-note">Weakest subjects (they get extra sessions):</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {profile.subjects.map((s) => (
              <button key={s} className={`admin-btn ${profile.weak_subjects.includes(s) ? "primary" : ""}`}
                      onClick={() => setProfile((p) => ({ ...p, weak_subjects: toggle(p.weak_subjects, s) }))} data-testid={`wz-weak-${s}`}>{s}</button>
            ))}
          </div>
          {profile.subjects.map((s) => (
            <label key={s} className="tp-label">{s.toUpperCase()} CHAPTERS (comma separated — revise these first)
              <input className="admin-input" placeholder={s === "Physics" ? "e.g. Laws of Motion, Work Energy Power" : "e.g. chapter names as per your coaching"}
                     value={(profile.chapters[s] || []).join(", ")}
                     onChange={(e) => setProfile((p) => ({ ...p, chapters: { ...p.chapters, [s]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } }))}
                     data-testid={`wz-chapters-${s}`} /></label>
          ))}
        </div>
      )}

      {step === 4 && (
        <div className="tp-panel" style={{ textAlign: "center", padding: 30 }}>
          <Sparkles size={30} color="#f77f00" />
          <h2 className="tp-h2" style={{ fontSize: 22 }}>Ready, {profile.name.split(" ")[0]}?</h2>
          <p className="tp-muted">I'll build a week around YOUR routine — revision → practice → homework, rotating {profile.subjects.join(", ")}, weakest first. Sundays stay light (revision only).</p>
          <button className="tp-btn" style={{ background: "#f77f00" }} onClick={generate} disabled={busy} data-testid="wz-generate">
            {busy ? "Building your timetable…" : "GENERATE MY TIMETABLE"}</button>
        </div>
      )}
      {error && <p className="tp-error" role="alert" data-testid="wz-error">{error}</p>}
      <div className="admin-row" style={{ marginTop: 16, justifyContent: "space-between" }}>
        <button className="admin-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="wz-back">← Back</button>
        {step < 4 && <button className="admin-btn primary" disabled={!canNext} onClick={() => setStep((s) => s + 1)} data-testid="wz-next">Next →</button>}
      </div>
    </div>
  );
}

function PlanView({ plan, onChange, onRegenerate }) {
  const [dayIdx, setDayIdx] = useState(Math.min((new Date().getDay() + 6) % 7, 6));
  const [progress, setProgress] = useState(null);
  const [msg, setMsg] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;
  const refreshProgress = () => hwApi.get("/planner/plan/progress").then(({ data }) => setProgress(data)).catch(() => {});
  useEffect(() => { refreshProgress(); }, []);
  const day = plan.days[dayIdx];

  const toggle = async (slot) => {
    setMsg("");
    try {
      await hwApi.patch("/planner/plan/check", { day_idx: day.day_idx, slot_id: slot.slot_id, done: !slot.done });
      slot.done = !slot.done;
      onChange({ ...plan });
      refreshProgress();
    } catch (e) { setMsg(hwErr(e)); }
  };
  const reschedule = async (slot) => {
    setMsg("");
    try {
      const { data } = await hwApi.post("/planner/plan/reschedule", { day_idx: day.day_idx, slot_id: slot.slot_id });
      setMsg(`Moved to ${data.day} ${data.start_label}`);
      const { data: fresh } = await hwApi.get("/planner/plan");
      onChange(fresh);
    } catch (e) { setMsg(hwErr(e)); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 860 }} data-testid="planner-view">
      <div className="tp-eyebrow">MY STUDY TIMETABLE</div>
      <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <h1 className="tp-h2" style={{ fontSize: 26, margin: "8px 0" }}>{plan.profile.name}&apos;s week — {plan.profile.exam_target}</h1>
        <div className="admin-row">
          <a className="tp-btn" style={{ background: "#0b2545", textDecoration: "none" }} href={`${backend}/api/hw/planner/plan/pdf`} target="_blank" rel="noreferrer" data-testid="planner-pdf"><Download size={15} /> Download PDF</a>
          <button className="tp-btn ghost" onClick={onRegenerate} data-testid="planner-regen"><RotateCcw size={14} /> Rebuild</button>
        </div>
      </div>
      {(plan.warnings || []).map((w, i) => <p key={i} className="tp-note" style={{ color: "#b45309" }} data-testid={`planner-warning-${i}`}>{w}</p>)}
      {progress && (
        <div className="tp-cards" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", margin: "6px 0 16px" }} data-testid="planner-progress">
          <div className="tp-card"><h3 data-testid="planner-pct">{progress.completion_pct}%</h3><p>week completed</p></div>
          <div className="tp-card"><h3>{Math.round(progress.minutes_done / 60 * 10) / 10}h</h3><p>of {Math.round(progress.minutes_total / 60)}h planned</p></div>
          <div className="tp-card"><h3>{progress.homework_tasks_done}</h3><p>homework tasks done</p></div>
          <div className="tp-card" data-testid="planner-streak"><h3 style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Flame size={21} color="#f77f00" /> {progress.streak}</h3><p>day streak · best {progress.best_streak}</p></div>
        </div>
      )}
      {progress && (
        <div style={{ margin: "0 0 16px" }} data-testid="planner-milestones">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {MILESTONES.map((m) => {
              const earned = progress.best_streak >= m;
              return (
                <div key={m} data-testid={`planner-milestone-${m}`}
                     style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20,
                              fontSize: 12, fontWeight: 700, background: earned ? "#fff7ed" : "#f1f5f9",
                              border: earned ? "1.5px solid #f77f00" : "1.5px solid #e2e8f0",
                              color: earned ? "#b45309" : "#94a3b8" }}>
                  <Trophy size={13} color={earned ? "#f77f00" : "#cbd5e1"} />
                  {m}-day streak{earned ? "" : ` · ${m - progress.best_streak} to go`}
                </div>
              );
            })}
          </div>
          {progress.streak >= 7 && (
            <p className="tp-pill ok" style={{ marginTop: 10, display: "inline-block" }} data-testid="planner-milestone-banner">
              {MILESTONES.filter((m) => progress.streak >= m).slice(-1)[0]}-day streak — perfect study days, one after another. Pranjal Sir is watching. Keep the fire alive!
            </p>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14 }} data-testid="planner-days">
        {plan.days.map((d, i) => (
          <button key={d.day} className={`admin-btn ${i === dayIdx ? "primary" : ""}`} onClick={() => setDayIdx(i)} data-testid={`planner-day-${i}`}>
            {d.day.slice(0, 3)} ({d.slots.filter((s) => s.done).length}/{d.slots.length})
          </button>
        ))}
      </div>
      {msg && <p className="tp-note" data-testid="planner-msg">{msg}</p>}
      <div className="tp-panel">
        {!day.slots.length && <p className="tp-muted" data-testid="planner-empty-day">{day.day === "Sunday" ? "Sunday — light revision only. Rest, revise notes, prepare for the week." : "No study slots fit this day."}</p>}
        {day.slots.map((s, i) => (
          <div key={s.slot_id} className="admin-row" style={{ padding: "10px 4px", borderBottom: "1px solid #eef1f5", gap: 10, flexWrap: "wrap", opacity: s.done ? 0.55 : 1 }} data-testid={`planner-slot-${i}`}>
            <input type="checkbox" checked={!!s.done} onChange={() => toggle(s)} style={{ width: 18, height: 18, accentColor: "#15803d" }} data-testid={`planner-check-${i}`} />
            <span className="tp-pill info">{s.start_label}–{s.end_label}</span>
            <b style={{ fontSize: 13, minWidth: 76 }}>{s.subject}</b>
            <span style={{ flex: 1, fontSize: 13 }}>{s.kind} — <i>{s.chapter}</i></span>
            {!s.done && <button className="admin-btn" style={{ fontSize: 11 }} onClick={() => reschedule(s)} data-testid={`planner-resched-${i}`}>Reschedule</button>}
            {!s.done && s.kind.includes("Pranjal Sir Physics") && (
              <a className="admin-btn primary" style={{ fontSize: 11, textDecoration: "none" }}
                 href={`/homework/new?planner_day=${day.day_idx}&planner_slot=${s.slot_id}&chapter=${encodeURIComponent(s.chapter)}`}
                 data-testid={`planner-upload-${i}`}>Upload →</a>
            )}
            {s.done && <CheckCircle2 size={16} color="#15803d" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlannerInner() {
  const [plan, setPlan] = useState(undefined);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    hwApi.get("/planner/plan").then(({ data }) => setPlan(data)).catch(() => setPlan(null));
  }, []);
  if (plan === undefined) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;
  if (plan === null || editing) return <Wizard onDone={(p) => { setPlan(p); setEditing(false); }} />;
  return <PlanView plan={plan} onChange={setPlan} onRegenerate={() => setEditing(true)} />;
}

export default function Planner() {
  return <HomeworkShell><PlannerInner /></HomeworkShell>;
}
